import { DocumentViewer } from '@/components/layout/DocumentViewer';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSharedKnowledgeFileByIdService } from '@/services/sharedFiles';
import { browserDownload } from '@/utils/downloadFileUtils';
import { ArrowLeft, Download, FileText, Pencil, Trash } from 'lucide-react';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { DeleteFileModal } from './DeleteFileModal';
import { EditFileModal } from './EditFileModal';

export const FilePreviewPage: FC = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { file_id } = useParams();
  const { file, isLoading } = useSharedKnowledgeFileByIdService(file_id ?? '');

  const navigate = useNavigate();

  const navigateBack = () => {
    navigate(-1);
  };

  const handleDownload = () => {
    browserDownload(file?.signedUrl ?? '', file?.name ?? '');
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <FlexCol h="screen">
      <FlexRow
        p="6"
        gap="6"
        alignItems="center"
        justifyContent="between"
        className="border-b"
      >
        <FlexRow gap="4" alignItems="center">
          <Button variant="ghost" size="sm" onClick={navigateBack}>
            <ArrowLeft size={16} />
            <p className="text-sm">Back</p>
          </Button>
          <FileText />
          <FlexCol gap="2">
            <Ellipsis>
              <span className="text-2xl font-bold">{file?.name}</span>
            </Ellipsis>
            <FlexRow gap="2">
              {file?.group && (
                <Chip size="sm" interactive={false}>
                  <Chip.Label>{file.group.name}</Chip.Label>
                </Chip>
              )}
              {!file?.group && (
                <Chip size="sm" interactive={false}>
                  <Chip.Label>No Group</Chip.Label>
                </Chip>
              )}
            </FlexRow>
          </FlexCol>
        </FlexRow>
        <FlexRow gap="4">
          <FlexRow gap="2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleDownload}>
                  <Download />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleEdit}>
                  <Pencil />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit Name</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleDelete}>
                  <Trash />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </FlexRow>
        </FlexRow>
      </FlexRow>

      <DocumentViewer
        name={file?.name ?? ''}
        url={file?.signedUrl ?? undefined}
        mimeType={file?.mimeType ?? ''}
      />

      <DeleteFileModal
        fileId={file_id ?? ''}
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
      />

      <EditFileModal
        fileId={file_id ?? ''}
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        initialGroupId={file?.group?.id ?? null}
        initialName={file?.name ?? ''}
      />
    </FlexCol>
  );
};
